import { mixed, object } from 'yup'

import { convertBooleanToYesNo } from '../../../../helpers/utils'
import { YesNo } from '../../../../models'
import { EmploymentJob } from '../../../../models/employment'

export type EmploymentHistoryFmcsrForm = {
  dotNumber: string
  isOwnAuthority: YesNo | null
  mayContact: YesNo | null
  contactName: string
  phoneNumber: string
  email: string
  fmcsrValid: YesNo | null
  drugAlcohol: YesNo | null
  driverDocuments: (File | { filename: string; url: string })[]
}

export const getEmploymentHistoryFmcsrInitialForm = (item: EmploymentJob): EmploymentHistoryFmcsrForm => {
  return {
    dotNumber: item.details.dotNumber ?? '',
    isOwnAuthority: convertBooleanToYesNo(item.details.isOwnAuthority),
    mayContact: item.details.isCurrentlyWorkingHere
      ? convertBooleanToYesNo(item.details.canContactEmployer)
      : YesNo.YES,
    phoneNumber: item.details.contactPhone ?? '',
    contactName: item.details.contactName ?? '',
    email: item.details.contactEmail ?? '',
    fmcsrValid: convertBooleanToYesNo(item.details.subjectToFmcsrRegulations),
    drugAlcohol: convertBooleanToYesNo(item.details.subjectToDrugAndAlcoholTesting),
    driverDocuments: item.driverDocuments ?? [],
  }
}

export const getEmploymentHistoryFmcsrFormSchema = (item: EmploymentJob) =>
  object().shape({
    dotNumber: mixed().test(function (val) {
      if (!val || val.length > 8) {
        return this.createError({
          message: 'Enter a valid DOT number',
        })
      }
      return true
    }),
    isOwnAuthority: mixed().test(function (val) {
      if (!val) return this.createError({ message: 'Please make a selection' })
      return true
    }),
    mayContact: mixed().test(function (val) {
      if (this.parent.isOwnAuthority === YesNo.YES) return true
      if (item.details.isCurrentlyWorkingHere && !val)
        return this.createError({ message: 'Please make a selection' })
      return true
    }),
    phoneNumber: mixed().test(function (val) {
      if (this.parent.isOwnAuthority === YesNo.YES) return true
      if ((this.parent.mayContact === YesNo.YES || !item.details.isCurrentlyWorkingHere) && !val)
        return this.createError({ message: 'Enter the required information' })
      return true
    }),
    email: mixed().test(function (val) {
      // email is only going to error is it is a non-valid email, otherwise it is optional
      if (
        (this.parent.mayContact === YesNo.YES || !item.details.isCurrentlyWorkingHere) &&
        Boolean(val) &&
        !/^\S+@\S+\.\S+$/.test(val)
      )
        return this.createError({ message: 'Enter a valid email address' })
      return true
    }),
    driverDocuments: mixed().test(function (val) {
      if (this.parent.isOwnAuthority === YesNo.YES) return true
      if (this.parent.mayContact === YesNo.NO && item.details.isCurrentlyWorkingHere && (!val || !val.length))
        return this.createError({ message: 'This field is required' })
      return true
    }),
    fmcsrValid: mixed().required('Please make a selection'),
    drugAlcohol: mixed().required('Please make a selection'),
  })

export const getEmploymentHistoryFmcsrInitialErrors = (employmentHistoryItem: EmploymentJob) => {
  const errors: Array<string> = []
  const {
    details: {
      dotNumber,
      canContactEmployer,
      contactPhone,
      subjectToFmcsrRegulations,
      subjectToDrugAndAlcoholTesting,
      isOwnAuthority,
    },
    driverDocuments,
  } = employmentHistoryItem

  if (!dotNumber) errors.push(`Employer's DOT number`)
  if (!isOwnAuthority) {
    if (canContactEmployer) {
      if (!contactPhone) errors.push(`Contact phone number`)
    } else {
      if (!driverDocuments || !driverDocuments.length) errors.push(`Upload a job document`)
    }
  }
  if (subjectToFmcsrRegulations === null) errors.push(`Federal Motor Carrier Safety Regulations`)
  if (subjectToDrugAndAlcoholTesting === null) errors.push(`Drug and alcohol testing`)

  return errors
}
